import {Component, Input, OnInit, ViewChild} from "@angular/core";
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {UserInfoService} from "../../user-info.service";
import {AuthService, LanguageService} from "../../../../swaggerServices";
import { ClientNotificationService } from "../../client-notification.service";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { AuthenticationResult, InteractionStatus } from "@azure/msal-browser";
import { environment } from "../../../environments/environment";
import { firstValueFrom, Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";
import { ConfigStateService } from "../../config/config-state.service";
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import {Language_Local_Storage} from '../../../shared/types/language.enum';

declare const $: any;

@Component({
    selector: 'app-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
    providers: [
        {
            provide: TRANSLOCO_SCOPE,
            useValue: 'login'
        }
    ],
    standalone: false
})
export class LoginFormComponent implements OnInit {
  @Input() authExpired?: boolean;
  loginForm: UntypedFormGroup;
  @ViewChild('form', {static: true}) formEl;
  @ViewChild('username', {static: true}) usernameEl;
  errorMessage: string = '';

  ssoOnly: boolean = !(environment.msLogin && environment.adClientId);
  loggingIn: boolean = false;
  loginWithMS: boolean = false;
  msLoginAllowed: boolean = !!(environment.msLogin);
  subscriptions: Subscription[] = [];
  activeLang: string;

  constructor(private userInfo: UserInfoService,
              private authService: AuthService,
              private config: ConfigStateService,
              private clientNotificationService: ClientNotificationService,
              private msalService: MsalService,
              private msalBroadcastService: MsalBroadcastService,
              private languageService: LanguageService) { }

  private async ensureMsalReady(): Promise<void> {
    await firstValueFrom(
      this.msalBroadcastService.inProgress$.pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1)
      )
    );
  }

  ngOnInit() {
    this.subscriptions.push(this.config.loadConfig().subscribe());
    this.loginForm = new UntypedFormGroup({
      username: new UntypedFormControl("", {
        validators: [Validators.required],
      }),
      password: new UntypedFormControl("", {
        validators: [Validators.required],
      }),
    });
    this.usernameEl?.nativeElement.focus();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s => s.unsubscribe()));
  }

  signIn() {
    this.errorMessage = '';
    if (!this.loginForm.valid) {
      this.loginForm.get('username').markAsDirty();
      this.loginForm.get('password').markAsDirty();
      this.focusFirstInvalidInput();
    } else {
      this.loggingIn = true;
      const username = this.loginForm.get('username').value;
      const password = this.loginForm.get('password').value;
      this.subscriptions.push(this.authService.addAuth({username, password }).subscribe(authResp => {
        this.loginForm.reset({});
        this.loggingIn = false;
        if (authResp.success) {
          this.userInfo.setInfoFromToken(authResp.token);
          if (!this.userInfo.subscribeRunning) {
            this.userInfo.subscribeRunning = true;
            this.clientNotificationService.connect(this.userInfo);
          }
        }
        this.languageService.storeLanguageType({language: window.localStorage.getItem(Language_Local_Storage)}).subscribe()
      },
      () => {
        this.loggingIn = false;
      }));
    }
  }

  public async microsoftSignIn() {
    this.loggingIn = true;
    this.loginWithMS = true;

    try {
      await this.ensureMsalReady();
    } catch {
      this.loggingIn = false;
      return;
    }

    const rq = {
      scopes: [`${environment.adClientId}/.default`, 'email'],
    };
    this.subscriptions.push(this.msalService.loginPopup(rq).subscribe((response: AuthenticationResult) => {
      if (response && response.idToken && response.accessToken && response.account?.homeAccountId) {
        this.subscriptions.push(this.authService.adLogin({ idToken: response.idToken }).subscribe(
          authResp => {
            this.loggingIn = false;
            this.loginWithMS = false;
            if (authResp.success) {
              this.msalService.instance.setActiveAccount(response.account);
              this.userInfo.setAdTokens({
                idToken: response.idToken,
                accessToken: response.accessToken,
                homeAccountId: response.account.homeAccountId,
                username: response.account.username,
              });
              this.userInfo.setInfoFromToken(authResp.token);
              if (!this.userInfo.subscribeRunning) {
                this.userInfo.subscribeRunning = true;
                this.clientNotificationService.connect(this.userInfo);
              }
            }
            this.languageService.storeLanguageType({ language: window.localStorage.getItem(Language_Local_Storage) }).subscribe()
          },
          () => {
            this.loggingIn = false;
            this.loginWithMS = false;
          }
        ));
      }
    },
      () => {
        this.loggingIn = false;
        this.loginWithMS = false;
      }));
  }


  focusFirstInvalidInput() {
    const invalidEls = $(this.formEl.nativeElement).find('input.ng-invalid');
    if (invalidEls) {
      invalidEls[0].focus();
    }
  }
}
